import { CardSize, Quadrant } from 'src/app/types/enums'

/** match dashboard-grid CSS (row height + gap) */
export const GRID_ROW_HEIGHT_PX = 400
export const GRID_GAP_PX = 8

/** how near (in column-widths) the pointer must be to an internal boundary to trigger a resize drop */
const BOUNDARY_ZONE = 0.1

/** how near (as a fraction of a small cell's width/height) the pointer must be to the cell's
 *  dead centre to trigger the whole (small) cell drop, rather than an `extraSmall` quadrant */
const QUADRANT_CENTER_ZONE = 0.15

export interface GridLayoutItem {
  row: number
  colStart: number
  cols: number
}

/** a single grid slot - normally one card, but a `small` slot can be shared by up to 4
 *  `extraSmall` metrics (a "quad group") occupying one quadrant each */
export interface FlowUnit<T> {
  cols: number
  cards: T[]
}

interface FlowUnitCard {
  cols: number
  size: CardSize
  quadGroupId?: number
}

/** groups consecutive `extraSmall` cards that share the same quadGroupId into a single
 *  cols=1 flow unit, so they occupy one grid slot instead of one each */
export function groupIntoFlowUnits<T extends FlowUnitCard>(cards: T[]): FlowUnit<T>[] {
  const units: FlowUnit<T>[] = []

  cards.forEach((card) => {
    const last = units[units.length - 1]
    const lastCard = last?.cards[0]
    const canJoinLast =
      card.size === CardSize.extraSmall &&
      card.quadGroupId != null &&
      lastCard?.size === CardSize.extraSmall &&
      lastCard.quadGroupId === card.quadGroupId

    if (canJoinLast && last) {
      last.cards.push(card)
    } else {
      units.push({ cols: card.cols, cards: [card] })
    }
  })

  return units
}

/** converts an insert index within flow units back into an index within the flat cards array */
export function unitIndexToCardIndex<T>(units: FlowUnit<T>[], unitIndex: number): number {
  let index = 0
  for (let i = 0; i < unitIndex && i < units.length; i++) index += units[i].cards.length
  return index
}

/** finds the flow unit occupying a given row/colStart, if any (used to detect whether a drop
 *  target is empty, holds a quad group with room, or is already occupied by a whole card) */
export function findUnitAt<T>(
  layout: GridLayoutItem[],
  units: FlowUnit<T>[],
  row: number,
  colStart: number
): FlowUnit<T> | null {
  const index = layout.findIndex((item) => item.row === row && item.colStart === colStart)
  return index > -1 ? units[index] : null
}

export function spanForSize(size: CardSize, totalCols: number): number {
  switch (size) {
    case CardSize.large:
      return totalCols
    case CardSize.medium:
      return Math.min(2, totalCols)
    default:
      return 1
  }
}

export function sizeForSpan(span: number, totalCols: number): CardSize {
  if (span >= totalCols) return CardSize.large
  if (span >= 2) return CardSize.medium
  return CardSize.small
}

/** simulates the same row-major auto-flow wrapping used by CSS grid-auto-flow: row */
export function computeLayout(items: { cols: number }[], totalCols: number): GridLayoutItem[] {
  const layout: GridLayoutItem[] = []
  let row = 0
  let colCursor = 0

  items.forEach((item) => {
    const cols = Math.min(item.cols, totalCols)
    if (colCursor + cols > totalCols) {
      row++
      colCursor = 0
    }
    layout.push({ row, colStart: colCursor, cols })
    colCursor += cols
  })

  return layout
}

/** given a pointer's fractional row/col position within the grid, determine the drop slot + size */
export function hitTestGrid(
  relY: number,
  relX: number,
  containerWidth: number,
  totalCols: number
): { row: number; colStart: number; span: number } {
  const row = Math.max(0, Math.floor(relY / (GRID_ROW_HEIGHT_PX + GRID_GAP_PX)))
  const colWidth = containerWidth / totalCols
  const colFraction = clamp(relX / colWidth, 0, totalCols)

  if (totalCols === 1) {
    return { row, colStart: 0, span: 1 }
  }

  const nearestBoundary = Math.round(colFraction)
  const distToBoundary = Math.abs(colFraction - nearestBoundary)
  const isInternalBoundary = nearestBoundary > 0 && nearestBoundary < totalCols

  if (isInternalBoundary && distToBoundary < BOUNDARY_ZONE) {
    if (nearestBoundary === totalCols / 2) {
      return { row, colStart: 0, span: totalCols } // centre boundary -> full row
    }
    return { row, colStart: nearestBoundary - 1, span: 2 } // pair boundary -> medium
  }

  const colIndex = clamp(Math.floor(colFraction), 0, totalCols - 1)
  return { row, colStart: colIndex, span: 1 }
}

/** for a span=1 (small) cell, resolves which corner quadrant the pointer is near (extraSmall
 *  metric drop), or null if it's nearer the centre (whole small cell). Only meaningful when
 *  hitTestGrid resolved a span=1 hit - callers must check that first. */
export function hitTestQuadrant(
  relY: number,
  relX: number,
  containerWidth: number,
  totalCols: number
): Quadrant | null {
  const rowHeight = GRID_ROW_HEIGHT_PX + GRID_GAP_PX
  const colWidth = containerWidth / totalCols

  const xInCol = relX - Math.floor(relX / colWidth) * colWidth
  const yInRow = relY - Math.max(0, Math.floor(relY / rowHeight)) * rowHeight

  const xFrac = clamp(xInCol / colWidth, 0, 1)
  const yFrac = clamp(yInRow / GRID_ROW_HEIGHT_PX, 0, 1)

  // only the dead centre (equidistant from all 4 quadrants) falls through to the whole-cell
  // drop - everywhere else, including along the boundary between two quadrants, snaps to
  // whichever quadrant the pointer leans towards
  const nearCenterX = Math.abs(xFrac - 0.5) < QUADRANT_CENTER_ZONE
  const nearCenterY = Math.abs(yFrac - 0.5) < QUADRANT_CENTER_ZONE
  if (nearCenterX && nearCenterY) return null

  const isRight = xFrac > 0.5
  const isBottom = yFrac > 0.5
  if (!isBottom && !isRight) return Quadrant.TopLeft
  if (!isBottom && isRight) return Quadrant.TopRight
  if (isBottom && !isRight) return Quadrant.BottomLeft
  return Quadrant.BottomRight
}

/** finds the array index (within items, excluding the dragged item) to insert the dragged item before */
export function resolveInsertIndex(
  layout: GridLayoutItem[],
  targetRow: number,
  targetColStart: number
): number {
  const index = layout.findIndex(
    (item) => item.row > targetRow || (item.row === targetRow && item.colStart >= targetColStart)
  )
  return index === -1 ? layout.length : index
}

/** matches the .quad-grid CSS gap between quadrant cells */
export const QUAD_GAP_PX = 4

/** pixel offset of a quadrant's top-left corner relative to its parent (`small`) cell's
 *  top-left corner, given that cell's rendered width - used to preview a metric sliding
 *  between quadrants (e.g. swapping with another metric) */
export function quadrantOffset(quadrant: Quadrant, colWidth: number): { x: number; y: number } {
  const halfWidth = (colWidth - QUAD_GAP_PX) / 2
  const halfHeight = (GRID_ROW_HEIGHT_PX - QUAD_GAP_PX) / 2
  const isRight = quadrant === Quadrant.TopRight || quadrant === Quadrant.BottomRight
  const isBottom = quadrant === Quadrant.BottomLeft || quadrant === Quadrant.BottomRight
  return {
    x: isRight ? halfWidth + QUAD_GAP_PX : 0,
    y: isBottom ? halfHeight + QUAD_GAP_PX : 0,
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}
